import React, { useEffect, useMemo, useState } from "react";
import * as Mui from "@mui/material";
import { useHistory } from "react-router-dom";
import {
  ArrowBack,
  ArrowForward,
  CheckCircle,
  EventRepeatOutlined,
  ExpandLess,
  ExpandMore,
  Search,
  SyncAlt,
} from "@mui/icons-material";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { RequestInfoBanner } from "components/DesignSystem/RequestInfoBanner";
import { detailCardSx } from "components/DesignSystem/RequestDetailField";
import {
  GlobalFieldLabel,
  globalTextFieldSx,
} from "components/DesignSystem/sharedUiPatterns";
import {
  PrimaryButton,
  SecondaryButton,
} from "components/CustomComponents/DesignSystemButtons";
import { bulkSelectionCheckboxSx } from "pages/TeamZone/Approve/approveUi";
import {
  designColors,
  designRadii,
  designType,
  designWeights,
} from "services/constants/designTokens";
import { useShiftSchedules } from "./useShiftSchedules";
import { formatDate, formatMoney } from "../Shared/formatters";
import { WizardStepper } from "../Shared/WizardStepper";
import { SelectField } from "../Shared/fieldInputs";
import {
  CellText,
  ColWidths,
  checkCellSx,
  compactCellSx as cellSx,
  compactHeadSx as headSx,
  fitTableSx,
  tableBoxSx,
} from "../Shared/tableUi";

// Change Payroll Cycle for the ticked Shift Earnings rows, from the mockup:
// 1 Select Employees & Records -> 2 Choose Payroll Cycle -> 3 Review &
// Confirm -> success summary. `rows` are the list rows in the current
// period (for "all records for this employee"); `selectedIds` are the rows
// ticked on the list. onApply(ids, cycle) returns { updated, skipped }.

const SHOW_OPTIONS = [
  { value: "selected", label: "Show: Selected only" },
  { value: "all", label: "Show: All records" },
];

const STEPS = ["Select Employees & Records", "Choose Payroll Cycle", "Review & Confirm"];

const fieldSx = {
  ...globalTextFieldSx,
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: designColors.teal,
  },
  "& .MuiInputBase-input": { fontSize: 14 },
};

// The record tables fit the standard panel width (see Shared/tableUi.js);
// the Review table's short headers stay on one line.
const reviewHeadSx = { ...headSx, whiteSpace: "nowrap" };

const initials = (name) =>
  String(name)
    .split(" ")
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

const Avatar = ({ name }) => (
  <Mui.Avatar
    sx={{
      width: 32,
      height: 32,
      fontSize: 12,
      fontWeight: designWeights.semibold,
      backgroundColor: designColors.tealTint,
      color: designColors.tealDark,
    }}
  >
    {initials(name)}
  </Mui.Avatar>
);

const cycleSummary = (records) => {
  const cycles = [...new Set(records.map((row) => row.payroll_cycle))];
  return cycles.length === 1 ? cycles[0] : cycles.join(", ");
};

// ---- Step 1 ----

const EmployeeGroup = ({ group, state, show, onChange, typeColumn }) => {
  const [open, setOpen] = useState(true);
  const chosen = state.mode === "all" ? group.records.map((row) => row.id) : state.ids;
  const visible =
    show === "selected"
      ? group.records.filter((row) => chosen.includes(row.id))
      : group.records;

  const toggleRecord = (id) =>
    onChange({
      ...state,
      mode: "selected",
      ids: chosen.includes(id) ? chosen.filter((item) => item !== id) : [...chosen, id],
    });

  return (
    <Mui.Box sx={{ ...detailCardSx, p: 0, overflow: "hidden" }}>
      <Mui.Stack direction="row" spacing={1.5} alignItems="center" sx={{ px: 2, py: 1.25 }}>
        <Mui.Checkbox
          size="small"
          sx={{ ...bulkSelectionCheckboxSx, p: 0 }}
          checked={state.included}
          onChange={() => onChange({ ...state, included: !state.included })}
          inputProps={{ "aria-label": `Include ${group.name}` }}
        />
        <Avatar name={group.name} />
        <Mui.Box sx={{ minWidth: 0, flex: 1 }}>
          <Mui.Typography sx={{ ...designType.tableBodyCell, fontWeight: designWeights.semibold }} noWrap>
            {group.name}{" "}
            <Mui.Box component="span" sx={{ ...designType.captionText }}>
              ({group.code})
            </Mui.Box>
          </Mui.Typography>
        </Mui.Box>
        <Mui.Typography sx={{ ...designType.captionText, whiteSpace: "nowrap" }}>
          {state.included ? chosen.length : 0} records selected (of {group.records.length})
        </Mui.Typography>
        <Mui.IconButton size="small" onClick={() => setOpen((value) => !value)} aria-label="Toggle records">
          {open ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
        </Mui.IconButton>
      </Mui.Stack>
      <Mui.Collapse in={open && state.included}>
        <Mui.Box sx={{ px: 2, pb: 2 }}>
          <Mui.RadioGroup
            row
            value={state.mode}
            onChange={(event) => onChange({ ...state, mode: event.target.value })}
            sx={{ flexWrap: { xs: "wrap", sm: "nowrap" }, columnGap: 3, mb: 1 }}
          >
            {[
              ["selected", `Selected records (${state.ids.length})`],
              ["all", `All records in this period (${group.records.length})`],
            ].map(([value, label]) => (
              <Mui.FormControlLabel
                key={value}
                value={value}
                control={<Mui.Radio size="small" sx={bulkSelectionCheckboxSx} />}
                label={label}
                sx={{ m: 0, whiteSpace: "nowrap", "& .MuiFormControlLabel-label": designType.helperText }}
              />
            ))}
          </Mui.RadioGroup>
          <Mui.TableContainer sx={tableBoxSx}>
            <Mui.Table size="small" sx={fitTableSx}>
              <ColWidths widths={[48, 96, null, null, 100, 88]} />
              <Mui.TableHead>
                <Mui.TableRow>
                  <Mui.TableCell padding="checkbox" sx={{ ...headSx, ...checkCellSx }} />
                  <Mui.TableCell sx={headSx}>Date</Mui.TableCell>
                  <Mui.TableCell sx={headSx}>{typeColumn.label}</Mui.TableCell>
                  <Mui.TableCell sx={headSx}>Shift</Mui.TableCell>
                  <Mui.TableCell sx={{ ...headSx, whiteSpace: "nowrap" }} align="right">Working Hrs</Mui.TableCell>
                  <Mui.TableCell sx={headSx} align="right">Payable</Mui.TableCell>
                </Mui.TableRow>
              </Mui.TableHead>
              <Mui.TableBody>
                {visible.map((row) => (
                  <Mui.TableRow key={row.id}>
                    <Mui.TableCell padding="checkbox" sx={{ ...cellSx, ...checkCellSx }}>
                      <Mui.Checkbox
                        size="small"
                        sx={{ ...bulkSelectionCheckboxSx, p: 0.5 }}
                        checked={chosen.includes(row.id)}
                        onChange={() => toggleRecord(row.id)}
                        inputProps={{ "aria-label": `Select ${formatDate(row.date)}` }}
                      />
                    </Mui.TableCell>
                    <Mui.TableCell sx={cellSx}>{formatDate(row.date)}</Mui.TableCell>
                    <Mui.TableCell sx={cellSx}><CellText text={row[typeColumn.key]} /></Mui.TableCell>
                    <Mui.TableCell sx={cellSx}><CellText text={row.shift_name} /></Mui.TableCell>
                    <Mui.TableCell sx={cellSx} align="right">{row.worked_shift_hours || "-"}</Mui.TableCell>
                    <Mui.TableCell sx={cellSx} align="right">{formatMoney(row.payable_amount)}</Mui.TableCell>
                  </Mui.TableRow>
                ))}
              </Mui.TableBody>
            </Mui.Table>
          </Mui.TableContainer>
        </Mui.Box>
      </Mui.Collapse>
    </Mui.Box>
  );
};

// ---- Wizard ----

// recordLabel / recordsLabel / screenLabel name the records in the texts and
// typeColumn is the record-type column (Pay Inputs > Leave and Penalties
// reuse this wizard, e.g. "penalty" / "penalties" / "Penalties" and
// { key: "penalty_type", label: "Penalty Type" }).
export const ChangePayrollCycle = ({ open, rows, selectedIds, onClose, onApply, domain, recordLabel = "shift earning", recordsLabel = `${recordLabel}s`, screenLabel = "Shift Earnings", typeColumn = { key: "earning_type", label: "Day Type" } }) => {
  const history = useHistory();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [groupState, setGroupState] = useState({});
  const [query, setQuery] = useState("");
  const [show, setShow] = useState("selected");
  const [cycle, setCycle] = useState("");
  const [result, setResult] = useState(null);

  // One group per employee that has a ticked row; each group lists that
  // employee's rows in the period that share the ticked rows' payroll cycle
  // (the list only lets one cycle be ticked at a time).
  const groups = useMemo(() => {
    const ticked = rows.filter((row) => selectedIds.includes(row.id));
    const cycles = new Set(ticked.map((row) => String(row.pay_schedule_id ?? "")));
    const codes = [...new Set(ticked.map((row) => row.employee_code))];
    return codes.map((code) => {
      const records = rows.filter((row) => row.employee_code === code && cycles.has(String(row.pay_schedule_id ?? "")));
      return { code, name: records[0]?.employee_name ?? code, records };
    });
  }, [rows, selectedIds]);

  useEffect(() => {
    if (!open) return;
    setStep(0);
    setError("");
    setQuery("");
    setShow("selected");
    setCycle("");
    setResult(null);
    setGroupState(
      Object.fromEntries(
        groups.map((group) => [
          group.code,
          {
            included: true,
            mode: "selected",
            ids: group.records.filter((row) => selectedIds.includes(row.id)).map((row) => row.id),
          },
        ]),
      ),
    );
    // Only reset when the panel opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const chosenRecords = useMemo(
    () =>
      groups.flatMap((group) => {
        const state = groupState[group.code];
        if (!state?.included) return [];
        return state.mode === "all"
          ? group.records
          : group.records.filter((row) => state.ids.includes(row.id));
      }),
    [groups, groupState],
  );

  const { schedules, error: scheduleError, loading: schedulesLoading, retry: retrySchedules } = useShiftSchedules(domain, chosenRecords.map((row) => row.employee_code), open);

  const perEmployee = groups
    .map((group) => ({
      ...group,
      chosen: chosenRecords.filter((row) => row.employee_code === group.code),
    }))
    .filter((group) => group.chosen.length);

  const fromCycle = cycleSummary(chosenRecords);
  const toMove = chosenRecords.filter((row) => String(row.pay_schedule_id) !== cycle);
  const alreadyThere = chosenRecords.length - toMove.length;

  const visibleGroups = groups.filter((group) => {
    const needle = query.trim().toLowerCase();
    return (
      !needle ||
      group.name.toLowerCase().includes(needle) ||
      group.code.toLowerCase().includes(needle)
    );
  });

  // A cycle every chosen record is already in would move nothing, so it is
  // left out of the list.
  const cycleOptions = schedules.filter((option) => !chosenRecords.every((row) => String(row.pay_schedule_id) === option.value));
  const selectedCycleAvailable = !schedulesLoading && !scheduleError && cycleOptions.some((option) => option.value === cycle);
  const cycleName = schedules.find((option) => option.value === cycle)?.label || cycle;

  const canNext =
    (step === 0 && chosenRecords.length > 0) || (step === 1 && selectedCycleAvailable && toMove.length > 0);

  const apply = async () => {
    if (saving || !selectedCycleAvailable) return;
    setSaving(true); setError("");
    try {
      const outcome = await onApply(chosenRecords.map((row) => row.id), cycle);
      setResult({ ...outcome, from: fromCycle, to: cycleName, employees: perEmployee.length });
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const footer = result ? (
    <Mui.Stack direction="row" justifyContent="flex-end" sx={{ width: "100%" }}>
      <PrimaryButton onClick={onClose}>Done</PrimaryButton>
    </Mui.Stack>
  ) : (
    <Mui.Stack
      direction={{ xs: "column-reverse", sm: "row" }}
      spacing={1.5}
      justifyContent="space-between"
      sx={{ width: "100%" }}
    >
      <SecondaryButton disabled={saving} onClick={onClose}>Cancel</SecondaryButton>
      <Mui.Stack direction={{ xs: "column-reverse", sm: "row" }} spacing={1.5}>
        {step > 0 ? (
          <SecondaryButton disabled={saving} startIcon={<ArrowBack />} onClick={() => setStep(step - 1)}>
            Back
          </SecondaryButton>
        ) : null}
        {step < 2 ? (
          <PrimaryButton endIcon={<ArrowForward />} disabled={!canNext} onClick={() => setStep(step + 1)}>
            Next
          </PrimaryButton>
        ) : (
          <PrimaryButton startIcon={<EventRepeatOutlined />} disabled={saving || !toMove.length || !selectedCycleAvailable} loading={saving} onClick={apply}>
            Update Payroll Cycle
          </PrimaryButton>
        )}
      </Mui.Stack>
    </Mui.Stack>
  );

  return (
    <GlobalSidePanel
      open={open}
      onClose={saving ? undefined : onClose}
      title="Change Payroll Cycle"
      subtitle={`Move the selected ${recordsLabel} to another payroll cycle.`}
      // Payroll -> Payroll home; the screen name -> back to its list (closes
      // this panel). Not while the change is being saved.
      breadcrumb={[
        { label: "Payroll", onClick: () => { if (!saving) history.push("/admin/payroll/setup"); } },
        "Pay Inputs",
        { label: screenLabel, onClick: () => { if (!saving) onClose(); } },
        "Change Payroll Cycle",
      ]}
      bodySx={{ backgroundColor: designColors.pageBg, p: { xs: 2, sm: 3 } }}
      footer={footer}
    >
      {error ? <Mui.Alert severity="error" sx={{ mb: 2 }}>{error}</Mui.Alert> : null}
      {scheduleError ? <Mui.Alert severity="warning" sx={{ mb: 2 }} action={<Mui.Button onClick={retrySchedules}>Retry</Mui.Button>}>{scheduleError}</Mui.Alert> : null}
      {result?.errors?.length ? <Mui.Alert severity="warning" sx={{ mb: 2 }}>{result.errors.map((item) => "Record " + item.id + ": " + item.message).join(" ")}</Mui.Alert> : null}
      {result ? (
        <Mui.Stack alignItems="center" spacing={2} sx={{ ...detailCardSx, py: 5, textAlign: "center" }}>
          <CheckCircle sx={{ fontSize: 64, color: designColors.teal }} />
          <Mui.Box>
            <Mui.Typography sx={designType.panelTitle}>{result.skipped ? "Payroll Cycle Update Results" : "Payroll Cycle Updated"}</Mui.Typography>
            <Mui.Typography sx={{ ...designType.helperText, mt: 0.5 }}>
              {result.updated} record(s) updated. {result.skipped} record(s) were not updated.
            </Mui.Typography>
          </Mui.Box>
          <Mui.Box
            sx={{
              width: "100%",
              maxWidth: 420,
              p: 2,
              borderRadius: designRadii.card,
              backgroundColor: designColors.slateBg,
              display: "grid",
              gridTemplateColumns: "1fr auto",
              rowGap: 1,
              textAlign: "left",
            }}
          >
            {[
              ["From Payroll Cycle", result.from],
              ["To Payroll Cycle", result.to],
              ["Employees Selected", result.employees],
              ["Records Updated", result.updated],
              ["Skipped Records", result.skipped],
            ].map(([label, value]) => (
              <React.Fragment key={label}>
                <Mui.Typography sx={designType.helperText}>{label}</Mui.Typography>
                <Mui.Typography sx={{ ...designType.tableBodyCell, fontWeight: designWeights.semibold }}>
                  {value}
                </Mui.Typography>
              </React.Fragment>
            ))}
          </Mui.Box>
        </Mui.Stack>
      ) : (
        <Mui.Stack spacing={2}>
          <Mui.Box sx={{ ...detailCardSx, py: 1.5 }}>
            <WizardStepper steps={STEPS} step={step} />
          </Mui.Box>

          {step === 0 ? (
            <>
              <Mui.Box>
                <Mui.Typography sx={designType.sectionHeading}>Select Employees and Records</Mui.Typography>
                <Mui.Typography sx={{ ...designType.helperText, mt: 0.25 }}>
                  Choose the employees and specific records to move to a new payroll cycle.
                </Mui.Typography>
              </Mui.Box>
              <Mui.Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                <Mui.TextField
                  size="small"
                  placeholder="Search employees by name or code..."
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  sx={{ ...fieldSx, flex: 1 }}
                  InputProps={{
                    startAdornment: (
                      <Mui.InputAdornment position="start">
                        <Search sx={{ fontSize: 18, color: designColors.slate400 }} />
                      </Mui.InputAdornment>
                    ),
                  }}
                />
                <Mui.Box sx={{ width: { xs: "100%", sm: 200 }, flexShrink: 0 }}>
                  <SelectField
                    value={show}
                    onChange={(value) => setShow(value || "selected")}
                    options={SHOW_OPTIONS}
                  />
                </Mui.Box>
              </Mui.Stack>
              {visibleGroups.map((group) =>
                groupState[group.code] ? (
                  <EmployeeGroup
                    key={group.code}
                    group={group}
                    show={show}
                    typeColumn={typeColumn}
                    state={groupState[group.code]}
                    onChange={(next) =>
                      setGroupState((current) => ({ ...current, [group.code]: next }))
                    }
                  />
                ) : null,
              )}
              {visibleGroups.length === 0 ? (
                <Mui.Typography sx={{ ...designType.helperText, textAlign: "center", py: 3 }}>
                  No employees match your search.
                </Mui.Typography>
              ) : null}
            </>
          ) : null}

          {step === 1 ? (
            <>
              <Mui.Box sx={detailCardSx}>
                <Mui.Typography sx={{ ...designType.sectionHeading, mb: 1.5 }}>
                  Selected Employees & Records
                </Mui.Typography>
                <Mui.TableContainer sx={tableBoxSx}>
                  <Mui.Table size="small" sx={fitTableSx}>
                    <ColWidths widths={[null, 96, 80, 140]} />
                    <Mui.TableHead>
                      <Mui.TableRow>
                        <Mui.TableCell sx={reviewHeadSx}>Employee</Mui.TableCell>
                        <Mui.TableCell sx={reviewHeadSx}>Code</Mui.TableCell>
                        <Mui.TableCell sx={reviewHeadSx} align="right">Records</Mui.TableCell>
                        <Mui.TableCell sx={reviewHeadSx}>Current Cycle</Mui.TableCell>
                      </Mui.TableRow>
                    </Mui.TableHead>
                    <Mui.TableBody>
                      {perEmployee.map((group) => (
                        <Mui.TableRow key={group.code}>
                          <Mui.TableCell sx={cellSx}>
                            <Mui.Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
                              <Avatar name={group.name} />
                              <CellText text={group.name} />
                            </Mui.Stack>
                          </Mui.TableCell>
                          <Mui.TableCell sx={cellSx}><CellText text={group.code} /></Mui.TableCell>
                          <Mui.TableCell sx={cellSx} align="right">{group.chosen.length}</Mui.TableCell>
                          <Mui.TableCell sx={cellSx}><CellText text={cycleSummary(group.chosen)} /></Mui.TableCell>
                        </Mui.TableRow>
                      ))}
                    </Mui.TableBody>
                  </Mui.Table>
                </Mui.TableContainer>
                <Mui.Box sx={{ display: "grid", gridTemplateColumns: "1fr", gap: 2, mt: 1.5 }}>
                  {[
                    ["Current Payroll Cycle", fromCycle],
                    ["Total Records to Move", chosenRecords.length],
                  ].map(([label, value]) => (
                    <Mui.Box key={label} sx={{ minWidth: 0 }}>
                      <Mui.Typography sx={designType.helperText}>{label}</Mui.Typography>
                      <Mui.Typography sx={{ ...designType.tableBodyCell, fontWeight: designWeights.semibold, color: designColors.slate900, overflowWrap: "anywhere" }}>
                        {value}
                      </Mui.Typography>
                    </Mui.Box>
                  ))}
                </Mui.Box>
              </Mui.Box>

              <Mui.Box sx={detailCardSx}>
                <Mui.Typography sx={{ ...designType.sectionHeading, mb: 1.5 }}>
                  Select New Payroll Cycle
                </Mui.Typography>
                <GlobalFieldLabel helperText="Payroll month the records will be paid in.">
                  Target Payroll Cycle <Mui.Box component="span" sx={{ color: "#DC2626" }}>*</Mui.Box>
                </GlobalFieldLabel>
                <Mui.Box sx={{ width: "100%" }}>
                  <SelectField
                    value={selectedCycleAvailable ? cycle : ""}
                    onChange={setCycle}
                    options={cycleOptions}
                    disabled={saving || schedulesLoading || Boolean(scheduleError)}
                    placeholder={schedulesLoading ? "Loading payroll cycles..." : "Select target payroll cycle"}
                  />
                </Mui.Box>
                {!schedulesLoading && !scheduleError && !cycleOptions.length ? <Mui.Typography sx={{ ...designType.helperText, mt: 1 }}>No common payroll cycle is available for the selected employees.</Mui.Typography> : null}
                {selectedCycleAvailable ? (
                  <Mui.Box sx={{ mt: 2 }}>
                    <RequestInfoBanner
                      body={`${toMove.length} ${recordLabel} record(s) will be moved to ${cycleName}.${
                        alreadyThere ? ` ${alreadyThere} already in ${cycle} will be skipped.` : ""
                      }`}
                    />
                  </Mui.Box>
                ) : null}
              </Mui.Box>
            </>
          ) : null}

          {step === 2 ? (
            <Mui.Box sx={detailCardSx}>
              <Mui.Typography sx={designType.sectionHeading}>Review Changes</Mui.Typography>
              <Mui.Typography sx={{ ...designType.helperText, mt: 0.25, mb: 2 }}>
                Review the selected records before updating the payroll cycle.
              </Mui.Typography>
              <Mui.Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(3, minmax(0, 1fr))" },
                  gap: 2,
                  p: 2,
                  mb: 2,
                  borderRadius: designRadii.card,
                  backgroundColor: designColors.slateBg,
                }}
              >
                {[
                  ["Employees", perEmployee.length],
                  ["Records to Move", toMove.length],
                  ["To Payroll Cycle", cycleName],
                ].map(([label, value]) => (
                  <Mui.Box key={label} sx={{ minWidth: 0 }}>
                    <Mui.Typography sx={designType.captionText}>{label}</Mui.Typography>
                    <Mui.Typography sx={{ ...designType.tableBodyCell, fontWeight: designWeights.semibold }} noWrap>
                      <CellText text={value} />
                    </Mui.Typography>
                  </Mui.Box>
                ))}
              </Mui.Box>
              <Mui.TableContainer sx={tableBoxSx}>
                <Mui.Table size="small" sx={fitTableSx}>
                  <ColWidths widths={[null, 64, 72, 112, 32, 96]} />
                  <Mui.TableHead>
                    <Mui.TableRow>
                      <Mui.TableCell sx={reviewHeadSx}>Employee</Mui.TableCell>
                      <Mui.TableCell sx={reviewHeadSx}>Code</Mui.TableCell>
                      <Mui.TableCell sx={reviewHeadSx} align="right">Records</Mui.TableCell>
                      <Mui.TableCell sx={reviewHeadSx}>Current Cycle</Mui.TableCell>
                      <Mui.TableCell sx={reviewHeadSx} />
                      <Mui.TableCell sx={reviewHeadSx}>New Cycle</Mui.TableCell>
                    </Mui.TableRow>
                  </Mui.TableHead>
                  <Mui.TableBody>
                    {perEmployee.map((group) => (
                      <Mui.TableRow key={group.code}>
                        <Mui.TableCell sx={cellSx}><CellText text={group.name} /></Mui.TableCell>
                        <Mui.TableCell sx={cellSx}><CellText text={group.code} /></Mui.TableCell>
                        <Mui.TableCell sx={cellSx} align="right">{group.chosen.length}</Mui.TableCell>
                        <Mui.TableCell sx={cellSx}><CellText text={cycleSummary(group.chosen)} /></Mui.TableCell>
                        <Mui.TableCell sx={{ ...cellSx, color: designColors.slate400 }}>
                          <SyncAlt fontSize="small" />
                        </Mui.TableCell>
                        <Mui.TableCell sx={{ ...cellSx, fontWeight: designWeights.semibold, color: designColors.tealDark }}>
                          <CellText text={cycleName} />
                        </Mui.TableCell>
                      </Mui.TableRow>
                    ))}
                  </Mui.TableBody>
                </Mui.Table>
              </Mui.TableContainer>
              <Mui.Box sx={{ mt: 2 }}>
                <RequestInfoBanner
                  body={
                    alreadyThere
                      ? `${alreadyThere} record(s) already in ${cycle} will be skipped.`
                      : "All selected records will be moved."
                  }
                />
              </Mui.Box>
            </Mui.Box>
          ) : null}
        </Mui.Stack>
      )}
    </GlobalSidePanel>
  );
};
